package sarka.design.skoria.messaging

import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow

/**
 * Single entry point for direct messaging.
 *
 * Owns the two things that shouldn't be spread across ViewModels: applying the
 * [MessageCodec] on every read and write, and keeping the socket's live stream
 * consistent with what the HTTP calls return.
 */
class MessageRepository(
    private val api: MessageApi = MessageApi(),
    private val dmClient: DmClient = DmClient(),
    private val codec: MessageCodec = PlaintextCodec(),
) {

    /** Live messages addressed to this user. Already decoded. */
    val incoming: SharedFlow<DirectMessage> get() = dmClient.incoming

    /** Delivery/read receipts for messages this user sent. */
    val receipts: SharedFlow<DmFrame.Receipt> get() = dmClient.receipts

    val socketConnected: StateFlow<Boolean> get() = dmClient.connected

    /** Safe to call repeatedly; the client ignores it when already running. */
    fun startListening() = dmClient.connect()

    fun stopListening() = dmClient.disconnect()

    suspend fun conversations(): Result<List<ConversationView>> =
        api.conversations().map { summaries ->
            summaries.map { summary ->
                ConversationView(
                    username = summary.username,
                    preview = codec.decodeOrUnsupported(
                        payload = summary.lastPayload,
                        version = summary.lastPayloadVersion,
                        sender = if (summary.lastMessageFromMe) "" else summary.username,
                    ).displayText(),
                    lastMessageAt = summary.lastMessageAt,
                    lastMessageFromMe = summary.lastMessageFromMe,
                    unreadCount = summary.unreadCount,
                )
            }
        }

    /**
     * History with [withUser], oldest first so it can be appended to directly.
     *
     * The server returns newest-first for paging; reversing here keeps that
     * detail out of the UI.
     */
    suspend fun conversation(
        withUser: String,
        limit: Int = 50,
        before: Long? = null,
    ): Result<ConversationPage> =
        api.conversation(withUser, limit, before).map { response ->
            ConversationPage(
                withUser = response.withUser,
                messages = response.messages.map { it.toView(myUsername = null, withUser = withUser) }.reversed(),
                hasMore = response.hasMore,
            )
        }

    suspend fun send(recipient: String, text: String): Result<MessageView> =
        api.send(
            recipient = recipient,
            payload = codec.encode(text, recipient),
            payloadVersion = codec.version,
        ).map { it.message.toView(myUsername = null, withUser = recipient) }

    suspend fun markRead(withUser: String): Result<Int> = api.markRead(withUser)

    suspend fun totalUnread(): Result<Int> = api.totalUnread()

    /** Decodes a live message from the socket. */
    fun decode(message: DirectMessage, withUser: String): MessageView =
        message.toView(myUsername = null, withUser = withUser)

    fun dispose() = dmClient.dispose()

    /**
     * [myUsername] is unused for now: whether a message is mine is derived from
     * comparing sender to [withUser], which avoids the repository needing to know
     * who is logged in. Kept in the signature because per-device E2E will need
     * the real identity to pick a decryption key.
     */
    private fun DirectMessage.toView(myUsername: String?, withUser: String): MessageView {
        val fromMe = !sender.equals(withUser, ignoreCase = true)
        return MessageView(
            id = id,
            fromMe = fromMe,
            text = codec.decodeOrUnsupported(payload, payloadVersion, sender).displayText(),
            sentAt = sentAt,
            delivered = deliveredAt != null,
            read = readAt != null,
        )
    }
}

/** A decoded message ready to render. */
data class MessageView(
    val id: String,
    val fromMe: Boolean,
    val text: String,
    val sentAt: Long,
    val delivered: Boolean = false,
    val read: Boolean = false,
)

/** A decoded conversation-list row. */
data class ConversationView(
    val username: String,
    val preview: String,
    val lastMessageAt: Long,
    val lastMessageFromMe: Boolean,
    val unreadCount: Int,
)

data class ConversationPage(
    val withUser: String,
    val messages: List<MessageView>,
    val hasMore: Boolean,
)

/**
 * Merges [new] into [existing], dropping duplicates by id and keeping the result
 * ordered oldest first.
 *
 * Needed because acknowledgements can be lost: if the socket drops between
 * receiving a message and acking it, the server replays that message on the next
 * connect. Without this the user would see it twice.
 */
fun mergeMessages(existing: List<MessageView>, new: List<MessageView>): List<MessageView> {
    if (new.isEmpty()) return existing

    val byId = LinkedHashMap<String, MessageView>(existing.size + new.size)
    for (message in existing) byId[message.id] = message
    // New wins on collision, so a replayed copy carrying delivered/read updates
    // replaces the stale one.
    for (message in new) byId[message.id] = message

    return byId.values.sortedBy { it.sentAt }
}
